// /utils/businessPrinter.js

import html2pdf from "html2pdf.js";

/**
 * Business Permit Application Printer
 * A reusable utility for printing business permit applications with iFrame
 */
export const businessApplicationPrinter = {
  /**
   * Print business permit application
   * @param {Object} printData - Business permit data
   * @param {Object} options - Printing options
   */
  print(printData, options = {}) {
    // Set default options
    const config = {
      logoUrl: options.logoUrl || null,
      showWatermark: options.showWatermark !== false,
      autoPrint: options.autoPrint !== false,
      paperSize: options.paperSize || 'A4',
      ...options
    };

    // Open iframe for printing
      if (config.method === 'pdf') {
      return this.exportToPDF(printData, config);
    } else {
      return  this.openPrintIframe(printData, config);
    }
  },

  /**
   * Open iframe and generate print document
   */
  openPrintIframe(data, config) {
    // Remove any existing iframe
    this.removeExistingIframe();

    // Create new iframe
    const iframe = this.createIframe();

    // Generate print HTML
    const printHTML = this.generatePrintHTML(data, config);

    // Write content to iframe
    const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(printHTML);
    iframeDoc.close();

    // Handle print after content loads
    this.handlePrint(iframe, config.autoPrint);
  },
   async exportToPDF(data, config) {
    try {
    const printHTML = this.generatePrintHTML(data, config);
      // Create temporary div for PDF generation
      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.innerHTML = printHTML;
      document.body.appendChild(tempDiv);

      // PDF options
      const pdfOptions = {
        margin: [15, 15, 15, 15],
        filename: `${config.filename}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#FFFFFF'
        },
        jsPDF: { 
          unit: 'mm', 
          format: config.paperSize.toLowerCase(),
          orientation: 'portrait'
        },
        pagebreak: {
          mode: ['avoid-all', 'css', 'legacy']
        }
      };

      // Generate PDF
      await html2pdf().from(tempDiv).set(pdfOptions).save();

      // Cleanup
      document.body.removeChild(tempDiv);
      
      return { success: true, message: 'PDF exported successfully' };
    } catch (error) {
      console.error('PDF export error:', error);
      return { success: false, message: 'Failed to export PDF' };
    }
  },

  /**
   * Remove existing print iframe
   */
  removeExistingIframe() {
    const existingIframe = document.getElementById('business-permit-print-iframe');
    if (existingIframe) {
      document.body.removeChild(existingIframe);
    }
  },

  /**
   * Create hidden iframe for printing
   */
  createIframe() {
    const iframe = document.createElement('iframe');
    iframe.id = 'business-permit-print-iframe';
    iframe.style.position = 'fixed';
    iframe.style.top = '-10000px';
    iframe.style.left = '-10000px';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    iframe.style.visibility = 'hidden';
    
    document.body.appendChild(iframe);
    return iframe;
  },

  /**
   * Handle print after iframe loads
   */
  handlePrint(iframe, autoPrint = true) {
    iframe.onload = () => {
      // Small delay to ensure content is fully rendered
      setTimeout(() => {
        iframe.contentWindow.focus();
        if (autoPrint) {
          iframe.contentWindow.print();
        }
      }, 300);
    };
  },

  /**
   * Generate complete print HTML
   */
  generatePrintHTML(data, config) {
    const printStyles = this.getPrintStyles(config);
    const printContent = this.generatePrintContent(data, config);

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Business Permit Application #${data.application?.application_id || 'N/A'}</title>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <style>${printStyles}</style>
      </head>
      <body>
        <div class="print-container">
          ${printContent}
        </div>
        
      </body>
      </html>
    `;
  },

  /**
   * Generate print content
   */
  generatePrintContent(data, config) {
    return `
      <!-- Header -->
      ${this.generateHeader(data, config)}
      
      <!-- Application Info -->
      ${this.generateApplicationInfo(data)}
      
      <!-- Business Information -->
      ${this.generateBusinessInfo(data)}
      
      <!-- Main Office Address -->
      ${this.generateMainOfficeAddress(data)}
      
      <!-- Owner Information -->
      ${this.generateOwnerInfo(data)}
      
      <!-- Owner Address -->
      ${this.generateOwnerAddress(data)}
      
      <!-- Contact Persons -->
      ${this.generateContacts(data.contacts)}
      
      <!-- Line of Business -->
      ${this.generateLineOfBusiness(data.permitLineOfBusinesses)}
      
      <!-- Business Operations -->
      ${this.generateBusinessOperations(data.businessInfo)}
      
      <!-- SOA & Balances -->
      ${this.generateSoaBalances(data.soaBalances)}
      
      <!-- Footer -->
      ${this.generateFooter()}
    `;
  },

  /**
   * Generate header section
   */
  generateHeader(data, config) {
    const logoHtml = config.logoUrl 
      ? `<img src="${config.logoUrl}" alt="LGU Logo" class="print-logo-img">`
      : `<div class="logo-placeholder">LGU LOGO</div>`;

    return `
      <div class="print-header">
        <div class="print-logo">
          ${logoHtml}
        </div>
        <div class="print-title">
          <h1>Business Permit Application</h1>
          <p class="subtitle">Office of the Business Permit and Licensing Office</p>
          ${config.showWatermark ? '<div class="print-watermark">OFFICIAL DOCUMENT</div>' : ''}
        </div>
      </div>
    `;
  },

  /**
   * Generate application info section
   */
  generateApplicationInfo(data) {
    return `
      <div class="meta-info">
        <div class="meta-grid">
          <div class="meta-item">
            <span class="meta-label">Application Number</span>
            <span class="meta-value">${data.application?.application_id || 'N/A'}</span>
          </div>
          <div class="meta-item">
            <span class="meta-label">Application Date</span>
            <span class="meta-value">${this.formatDate(data.application?.date_created)}</span>
          </div>
          <div class="meta-item">
            <span class="meta-label">BIN</span>
            <span class="meta-value">${data.application?.bin || 'Not Assigned'}</span>
          </div>
          <div class="meta-item">
            <span class="meta-label">Status</span>
            <span class="status-badge-print ${this.getStatusClass(data.application?.status)}">
              ${data.application?.status || 'No Status'}
            </span>
          </div>
        </div>
      </div>
    `;
  },

  /**
   * Generate business info section
   */
  generateBusinessInfo(data) {
    return `
      <div class="print-section">
        <div class="section-header">
          <div class="section-number">1</div>
          <h2 class="section-title">Business Information</h2>
        </div>
        
        <table class="data-table">
          <tbody>
            <tr>
              <td width="25%"><strong>Business Name:</strong></td>
              <td width="75%">${data.permitInfo?.business_name || 'N/A'}</td>
            </tr>
            <tr>
              <td><strong>Trade Name:</strong></td>
              <td>${data.permitInfo?.trade_name || 'N/A'}</td>
            </tr>
            <tr>
              <td><strong>Organization Type:</strong></td>
              <td>${data.permitInfo?.form_of_org || 'N/A'}</td>
            </tr>
            <tr>
              <td><strong>TIN Number:</strong></td>
              <td>${data.permitInfo?.tin || 'N/A'}</td>
            </tr>
            <tr>
              <td><strong>Registration No:</strong></td>
              <td>${data.permitInfo?.dti_sec_cda_reg_no || 'N/A'}</td>
            </tr>
            <tr>
              <td><strong>Registration Date:</strong></td>
              <td>${this.formatDate(data.permitInfo?.dti_sec_cda_reg_date)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    `;
  },

  /**
   * Generate main office address section
   */
  generateMainOfficeAddress(data) {
    const address = this.formatAddress(data.permitInfo, false);
    return `
      <div class="print-section">
        <div class="section-header">
          <div class="section-number">2</div>
          <h2 class="section-title">Main Office Address</h2>
        </div>
        
        <div class="address-card-print">
          ${address}
        </div>
      </div>
    `;
  },

  /**
   * Generate owner info section
   */
  generateOwnerInfo(data) {
    return `
      <div class="print-section">
        <div class="section-header">
          <div class="section-number">3</div>
          <h2 class="section-title">Owner Information</h2>
        </div>
        
        <table class="data-table">
          <tbody>
            <tr>
              <td width="25%"><strong>Owner Name:</strong></td>
              <td width="75%">${this.formatFullName(data.permitInfo)}</td>
            </tr>
            <tr>
              <td><strong>Citizenship:</strong></td>
              <td>${data.permitInfo?.owner_citizenship || 'N/A'}</td>
            </tr>
            <tr>
              <td><strong>Gender:</strong></td>
              <td>${data.permitInfo?.owner_sex || 'N/A'}</td>
            </tr>
          </tbody>
        </table>
      </div>
    `;
  },

  /**
   * Generate owner address section
   */
  generateOwnerAddress(data) {
    const address = this.formatAddress(data.permitInfo, true);
    return `
      <div class="print-section">
        <div class="section-header">
          <div class="section-number">4</div>
          <h2 class="section-title">Owner Address</h2>
        </div>
        
        <div class="address-card-print">
          ${address}
        </div>
      </div>
    `;
  },

  /**
   * Generate contacts section
   */
  generateContacts(contacts) {
    if (!contacts || contacts.length === 0) {
      return `
        <div class="print-section">
          <div class="section-header">
            <div class="section-number">5</div>
            <h2 class="section-title">Contact Persons (0)</h2>
          </div>
          <p class="no-data">No contacts added</p>
        </div>
      `;
    }

    const contactsHTML = contacts.map((contact, index) => `
      <div class="contact-card-print">
        <div class="contact-header-print">
          <div class="contact-avatar-print">
            ${this.getInitials(contact.firstname, contact.lastname)}
          </div>
          <div>
            <h3 style="margin: 0 0 5px 0;">Contact #${index + 1}</h3>
            <p style="margin: 0; color: #666; font-size: 10pt;">${this.formatContactName(contact)}</p>
          </div>
        </div>
        <table class="contact-table">
          <tbody>
            <tr>
              <td width="30%"><strong>Mobile:</strong></td>
              <td>${contact.mobile || 'N/A'}</td>
            </tr>
            <tr>
              <td><strong>Email:</strong></td>
              <td>${contact.email || 'N/A'}</td>
            </tr>
            <tr>
              <td><strong>Gender:</strong></td>
              <td>${contact.sex || 'N/A'}</td>
            </tr>
          </tbody>
        </table>
      </div>
    `).join('');

    return `
      <div class="print-section">
        <div class="section-header">
          <div class="section-number">5</div>
          <h2 class="section-title">Contact Persons (${contacts.length})</h2>
        </div>
        <div class="contacts-grid-print">
          ${contactsHTML}
        </div>
      </div>
    `;
  },

  /**
   * Generate line of business section
   */
  generateLineOfBusiness(lobs) {
    if (!lobs || lobs.length === 0) {
      return `
        <div class="print-section">
          <div class="section-header">
            <div class="section-number">6</div>
            <h2 class="section-title">Line of Business</h2>
          </div>
          <p class="no-data">No line of business added</p>
        </div>
      `;
    }

    const lobsHTML = lobs.map(lob => `
      <div class="lob-card-print">
        <div class="lob-header-print">
          <span class="lob-badge ${lob.main_or_other === 'Main' ? 'badge-main' : 'badge-other'}">
            ${lob.main_or_other || 'Other'}
          </span>
          <h3>${lob.line_of_business || 'N/A'}</h3>
        </div>
        <table class="lob-table">
          <tbody>
            <tr>
              <td width="30%"><strong>Sub Category:</strong></td>
              <td>${lob.sub_category || 'N/A'}</td>
            </tr>
            <tr>
              <td><strong>Products/Services:</strong></td>
              <td>${lob.product_services || 'N/A'}</td>
            </tr>
            <tr>
              <td><strong>No. of Units:</strong></td>
              <td>${lob.no_units || '0'}</td>
            </tr>
            <tr>
              <td><strong>${lob.type === 'Renewal' ? 'Gross Sales' : 'Capitalization'}:</strong></td>
              <td class="amount">₱${this.formatCurrency(lob.capitalization)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    `).join('');

    return `
      <div class="print-section">
        <div class="section-header">
          <div class="section-number">6</div>
          <h2 class="section-title">Line of Business (${lobs.length})</h2>
        </div>
        ${lobsHTML}
      </div>
    `;
  },

  /**
   * Generate business operations section
   */
  generateBusinessOperations(businessInfo) {
    if (!businessInfo) return '';

    const totalEmployees = (parseInt(businessInfo?.no_of_employees) || 0) + 
                          (parseInt(businessInfo?.no_females) || 0);

    return `
      <div class="print-section">
        <div class="section-header">
          <div class="section-number">7</div>
          <h2 class="section-title">Business Operations</h2>
        </div>
        
        <div class="operations-grid-print">
          <div class="operation-item-print">
            <span class="operation-label">Area (sqm)</span>
            <span class="operation-value">${businessInfo?.area_sqm || '0'}</span>
          </div>
          <div class="operation-item-print">
            <span class="operation-label">Total Employees</span>
            <span class="operation-value">${totalEmployees}</span>
          </div>
          <div class="operation-item-print">
            <span class="operation-label">Delivery Vehicles</span>
            <span class="operation-value">${businessInfo?.no_delivery_vehicles || '0'}</span>
          </div>
          <div class="operation-item-print">
            <span class="operation-label">LGU Employees</span>
            <span class="operation-value">${businessInfo?.no_emp_residing_lgu || '0'}</span>
          </div>
          <div class="operation-item-print">
            <span class="operation-label">Property Status</span>
            <span class="operation-value">${businessInfo?.rental_or_owned || 'N/A'}</span>
          </div>
        </div>
      </div>
    `;
  },

  /**
   * Generate SOA balances section
   */
  generateSoaBalances(soaBalances) {
    if (!soaBalances || soaBalances.length === 0) return '';

    const balancesHTML = soaBalances.map(bal => `
      <tr>
        <td>${this.formatDate(bal?.due_date)}</td>
        <td>SOA AMT: <strong>${this.formatCurrency(bal.soaAmount)}</strong></td>
        <td>BTAX: <strong>${this.formatCurrency(bal.amount)}</strong></td>
        <td>${bal.mode_of_payment || 'N/A'}</td>
        <td class="${bal.paid === 1 ? 'paid' : 'unpaid'}">
          ${bal.paid === 1 ? 'PAID' : 'UNPAID'}
        </td>
      </tr>
    `).join('');

    return `
      <div class="print-section">
        <div class="section-header">
          <div class="section-number">8</div>
          <h2 class="section-title">Statement of Account</h2>
        </div>
        
        <table class="soa-table">
          <thead>
            <tr>
              <th>Due Date</th>
              <th>SOA Amount</th>
              <th>Business Tax</th>
              <th>Payment Mode</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            ${balancesHTML}
          </tbody>
        </table>
      </div>
    `;
  },

  /**
   * Generate footer
   */
  generateFooter() {
    const now = new Date();
    return `
      <div class="print-footer">
        <div class="footer-content">
          <p>Document generated on ${now.toLocaleDateString()} at ${now.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</p>
          <p class="footer-note">This is an official document of the Business Permit and Licensing Office</p>
        </div>
      </div>
    `;
  },

  /**
   * Helper: Format date
   */
  formatDate(dateString) {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch {
      return dateString;
    }
  },

  /**
   * Helper: Format currency
   */
  formatCurrency(amount) {
    if (!amount) return '0.00';
    return parseFloat(amount).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  },

  /**
   * Helper: Get initials
   */
  getInitials(firstName, lastName) {
    const first = firstName ? firstName.charAt(0).toUpperCase() : '';
    const last = lastName ? lastName.charAt(0).toUpperCase() : '';
    return first + last || '?';
  },

  /**
   * Helper: Format full name
   */
  formatFullName(permitInfo) {
    const parts = [
      permitInfo?.owner_firstname,
      permitInfo?.owner_middlename,
      permitInfo?.owner_lastname,
      permitInfo?.owner_suffix
    ].filter(Boolean);
    return parts.join(' ') || 'N/A';
  },

  /**
   * Helper: Format contact name
   */
  formatContactName(contact) {
    const parts = [
      contact.firstname,
      contact.middlename,
      contact.lastname,
      contact.suffix
    ].filter(Boolean);
    return parts.join(' ') || 'N/A';
  },

  /**
   * Helper: Format address
   */
  formatAddress(permitInfo, isOwner = false) {
    const prefix = isOwner ? 'owner_' : '';
    const parts = [
      permitInfo?.[prefix + 'unit_no'],
      permitInfo?.[prefix + 'building_name'],
      permitInfo?.[prefix + 'block_no_lot_no'] ? `Block/Lot: ${permitInfo[prefix + 'block_no_lot_no']}` : null,
      permitInfo?.[prefix + 'street'],
      permitInfo?.[prefix + 'subdivision'],
      [permitInfo?.[prefix + 'barangay'], 
       permitInfo?.[prefix + 'city'], 
       permitInfo?.[prefix + 'province'],
       permitInfo?.[prefix + 'zip']
      ].filter(Boolean).join(', ')
    ].filter(Boolean);

    return parts.map(part => `<p class="address-line">${part}</p>`).join('');
  },

  /**
   * Helper: Get status class
   */
  getStatusClass(status) {
    if (!status) return 'status-draft';
    const map = {
      'Checking': 'status-checking',
      'Approved': 'status-approved',
      'Rejected': 'status-rejected',
      'Draft': 'status-draft',
      'Completed': 'status-completed',
      'Active': 'status-approved'
    };
    return map[status] || 'status-draft';
  },

  /**
   * Get print styles
   */
  getPrintStyles(config) {
    return `
      /* Print Styles */
      @media print {
        @page {
          size: ${config.paperSize};
          margin: 0.5in;
        }
        
        body {
          font-family: 'Segoe UI', Arial, sans-serif;
          font-size: 11pt;
          line-height: 1.4;
          color: #000;
          background: white !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        
        .page-break {
          page-break-after: always;
        }
        
        .print-only {
          display: block !important;
        }
        
        /* Hide interactive elements */
        button, .no-print {
          display: none !important;
        }
      }
      
      /* Base Styles */
      body {
        font-family: 'Segoe UI', Arial, sans-serif;
        margin: 0;
        padding: 20px;
        color: #333;
        background: white;
      }
      
      .print-container {
        max-width: 8.5in;
        margin: 0 auto;
        background: white;
      }
      
      /* Header */
      .print-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-bottom: 3px solid #dc3545;
        padding-bottom: 15px;
        margin-bottom: 25px;
        page-break-inside: avoid;
      }
      
      .print-title {
        text-align: center;
        flex-grow: 1;
      }
      
      .print-title h1 {
        font-size: 18pt;
        color: #dc3545;
        margin: 0 0 5px 0;
      }
      
      .print-title .subtitle {
        font-size: 11pt;
        color: #666;
        margin: 0;
      }
      
      .print-watermark {
        position: absolute;
        opacity: 0.1;
        font-size: 48pt;
        color: #dc3545;
        transform: rotate(-45deg);
        pointer-events: none;
        z-index: -1;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%) rotate(-45deg);
      }
      
      .print-logo {
        text-align: center;
        min-width: 100px;
      }
      
      .logo-placeholder {
        width: 80px;
        height: 80px;
        border: 2px solid #ddd;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 10pt;
        color: #999;
        margin: 0 auto;
      }
      
      .print-logo-img {
        width: 80px;
        height: 80px;
        object-fit: contain;
      }
      
      /* Meta Info */
      .meta-info {
        background: #f8f9fa;
        border-radius: 8px;
        padding: 15px;
        margin-bottom: 25px;
        border-left: 4px solid #dc3545;
        page-break-inside: avoid;
      }
      
      .meta-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 15px;
      }
      
      .meta-item {
        display: flex;
        flex-direction: column;
      }
      
      .meta-label {
        font-size: 9pt;
        color: #666;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 3px;
      }
      
      .meta-value {
        font-size: 11pt;
        font-weight: 600;
        color: #000;
      }
      
      /* Status Badges */
      .status-badge-print {
        display: inline-block;
        padding: 4px 12px;
        border-radius: 20px;
        font-size: 10pt;
        font-weight: 600;
      }
      
      .status-approved { background: #d4edda; color: #155724; }
      .status-checking { background: #fff3cd; color: #856404; }
      .status-rejected { background: #f8d7da; color: #721c24; }
      .status-draft { background: #e2e3e5; color: #383d41; }
      .status-completed { background: #cce5ff; color: #004085; }
      
      /* Sections */
      .print-section {
        margin-bottom: 30px;
        page-break-inside: avoid;
      }
      
      .section-header {
        background: #f8f9fa;
        padding: 10px 15px;
        border-left: 4px solid #dc3545;
        margin-bottom: 15px;
        display: flex;
        align-items: center;
      }
      
      .section-number {
        background: #dc3545;
        color: white;
        width: 30px;
        height: 30px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
        margin-right: 12px;
        font-size: 12pt;
      }
      
      .section-title {
        font-size: 14pt;
        color: #2c3e50;
        margin: 0;
        flex-grow: 1;
      }
      
      /* Tables */
      .data-table, .contact-table, .lob-table, .soa-table {
        width: 100%;
        border-collapse: collapse;
        margin: 10px 0;
        page-break-inside: avoid;
      }
      
      .data-table th, .soa-table th {
        background: #f8f9fa;
        padding: 10px;
        text-align: left;
        font-weight: 600;
        border-bottom: 2px solid #dee2e6;
        font-size: 10pt;
        color: #495057;
      }
      
      .data-table td, .contact-table td, .lob-table td, .soa-table td {
        padding: 12px 10px;
        border-bottom: 1px solid #e9ecef;
        vertical-align: top;
      }
      
      .data-table tr:last-child td,
      .contact-table tr:last-child td,
      .lob-table tr:last-child td,
      .soa-table tr:last-child td {
        border-bottom: none;
      }
      
      /* Address Cards */
      .address-card-print {
        background: #f8f9fa;
        padding: 15px;
        border-radius: 6px;
        border-left: 3px solid #007bff;
        margin: 10px 0;
        page-break-inside: avoid;
      }
      
      .address-line {
        margin: 3px 0;
        color: #495057;
      }
      
      /* Contact Cards */
      .contacts-grid-print {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
        gap: 15px;
        margin-top: 10px;
        page-break-inside: avoid;
      }
      
      .contact-card-print {
        border: 1px solid #dee2e6;
        border-radius: 8px;
        padding: 15px;
        background: white;
        page-break-inside: avoid;
      }
      
      .contact-header-print {
        display: flex;
        align-items: center;
        margin-bottom: 12px;
        padding-bottom: 10px;
        border-bottom: 1px dashed #dee2e6;
      }
      
      .contact-avatar-print {
        width: 40px;
        height: 40px;
        border-radius: 50%;
        background: linear-gradient(135deg, #6c757d, #495057);
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
        margin-right: 12px;
        font-size: 14pt;
      }
      
      /* LOB Cards */
      .lob-card-print {
        border: 1px solid #dee2e6;
        border-radius: 8px;
        padding: 15px;
        margin-bottom: 15px;
        background: white;
        page-break-inside: avoid;
      }
      
      .lob-header-print {
        display: flex;
        align-items: center;
        margin-bottom: 10px;
      }
      
      .lob-badge {
        display: inline-block;
        padding: 3px 10px;
        border-radius: 12px;
        font-size: 9pt;
        font-weight: 600;
        margin-right: 10px;
      }
      
      .badge-main { background: #28a745; color: white; }
      .badge-other { background: #6c757d; color: white; }
      
      .amount {
        color: #28a745;
        font-weight: 600;
      }
      
      /* Operations Grid */
      .operations-grid-print {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
        gap: 15px;
        margin-top: 15px;
      }
      
      .operation-item-print {
        background: #f8f9fa;
        padding: 15px;
        border-radius: 6px;
        text-align: center;
        border: 1px solid #e9ecef;
      }
      
      .operation-label {
        display: block;
        font-size: 10pt;
        color: #666;
        margin-bottom: 5px;
      }
      
      .operation-value {
        display: block;
        font-size: 16pt;
        font-weight: 600;
        color: #2c3e50;
      }
      
      /* SOA Table */
      .soa-table .paid {
        color: #28a745;
        font-weight: 600;
      }
      
      .soa-table .unpaid {
        color: #dc3545;
        font-weight: 600;
      }
      
      /* Footer */
      .print-footer {
        margin-top: 40px;
        padding-top: 20px;
        border-top: 2px solid #dee2e6;
        text-align: center;
        font-size: 9pt;
        color: #6c757d;
        page-break-inside: avoid;
      }
      
      .footer-note {
        font-style: italic;
        margin-top: 5px;
      }
      
      /* No Data */
      .no-data {
        text-align: center;
        color: #6c757d;
        font-style: italic;
        padding: 20px;
        background: #f8f9fa;
        border-radius: 6px;
      }
      
      /* Utilities */
      .text-right { text-align: right; }
      .text-center { text-align: center; }
      .font-bold { font-weight: 600; }
      .text-red { color: #dc3545; }
      .text-green { color: #28a745; }
      .mb-20 { margin-bottom: 20px; }
      .mt-20 { margin-top: 20px; }
      
      /* Page Breaks */
      @media print {
        .print-section:nth-child(4n) {
          page-break-before: always;
        }
      }
      
      /* Responsive */
      @media (max-width: 768px) {
        body { padding: 10px; }
        .contacts-grid-print { grid-template-columns: 1fr; }
        .meta-grid { grid-template-columns: 1fr; }
        .operations-grid-print { grid-template-columns: 1fr; }
        
        .print-header {
          flex-direction: column;
          text-align: center;
        }
        
        .print-logo {
          margin-bottom: 15px;
        }
      }
    `;
  }
};

// Default export
export default businessApplicationPrinter;